<template>
  <PageTemplate>
    <h1>Publications</h1>
    <div>
      <v-card title="Recently Updated Publications">
        <v-table
          :table="table"
          @showDoc="previewDoc"
          @downloadDoc="downloadDoc"
        />
      </v-card>
      <v-card
        title="Your Subscriptions"
        :padded="false"
      >
        <template #header>
          <v-button
            size="sm"
            @click="toSubscriptions"
          >
            Manage Subscriptions
          </v-button>
        </template>
        <section class="file-system">
          <div
            class="file-system__col"
            :class="{'file-system__col--selected': activeRootFolder !== null}"
          >
            <template v-if="rootFolders.length > 0">
              <v-folder
                v-for="(rootFolder, index) in rootFolders"
                :key="index"
                :title="rootFolder.name"
                :subtitle="rootFolder.subtitle"
                :is-selected="activeRootFolder === index"
                @click="loadSubFolder(rootFolder.id, index)"
              />
            </template>
            <div
              v-else
              class="file-system__message"
            >
              No folders available!
            </div>
          </div>
          <div
            v-if="activeRootFolder !== null"
            class="file-system__col"
            :class="{'file-system__col--selected': activeSubFolder !== null}"
          >
            <div class="file-system__nav">
              <button
                class="file-system__back"
                @click="clearSelectedRoot"
              >
                <v-icon type="angle-left" />
              </button>
              <v-folder
                :title="rootFolders[activeRootFolder].name"
                :subtitle="rootFolders[activeRootFolder].subtitle"
                is-selected
              />
            </div>
            <template v-if="subFolders.length > 0">
              <v-folder
                v-for="(subFolder, index) in subFolders"
                :key="index"
                :title="subFolder.name"
                :subtitle="subFolder.subtitle"
                :is-selected="activeSubFolder === index"
                @click="loadDocuments(subFolder.id, index)"
              />
            </template>
            <div
              v-else
              class="file-system__message"
            >
              No folders available!
            </div>
          </div>
          <div
            v-if="activeSubFolder !== null"
            class="file-system__col"
          >
            <div class="file-system__nav">
              <button
                class="file-system__back"
                @click="clearSelectedSub"
              >
                <v-icon type="angle-left" />
              </button>
              <v-folder
                :title="subFolders[activeSubFolder].name"
                :subtitle="subFolders[activeSubFolder].subtitle"
                is-selected
              />
            </div>
            <template v-if="documents.length > 0">
              <div class="file-system__header file-system__header--dark">
                <v-checkbox
                  id="file-system-all"
                  class="file-system__all"
                  :checked="checkDownloadAll"
                  @change="selectAllAction"
                >
                  Select all
                </v-checkbox>
              </div>
              <v-document
                v-for="(document, index) in documents"
                :id="document.id"
                :key="index"
                class="file-system__document"
                :title="document.name"
                :link="''+document.id"
                :is-selected="document.isSelected"
                :checkbox="true"
                :select-action="togglePublicationSelect"
                @click="downloadDoc"
              />
              <v-button-group class="file-system__actions">
                <v-button
                  :is-disabled="disableBulkDownload"
                  size="sm"
                  @click="downloadSelected()"
                >
                  Download Selected Files
                </v-button>
              </v-button-group>
            </template>
            <div
              v-else
              class="file-system__message"
            >
              No documents available!
            </div>
          </div>
        </section>
      </v-card>
    </div>
  </PageTemplate>
</template>
<script>
  import PageTemplate from "../templates/Page/index.vue";
  import Button from "@/atoms/Button";
  import Checkbox from "@/atoms/Checkbox";
  import Icon from "@/atoms/Icon";
  import Card from "@/molecules/Card";
  import Table from "@/molecules/Table";
  import Folder from "@/molecules/Folder"
  import ButtonGroup from "@/molecules/ButtonGroup";
  import Document from "@/molecules/Document";
  import {
    GET_PUBLICATION_CATEGORIES,
    GET_RECENT_PUBLICATIONS,
    GET_PUBLICATION_SUBFOLDERS,
    GET_SUBCAT_PUBLICATIONS,
    DOWNLOAD_PUBLICATION,
    PREVIEW_PUBLICATION,
    DOWNLOAD_BULK_PUBLICATION
  } from "../store/actions.type";

  import {columnFilter, columnSort, dateSort_ddmmyyyy} from "@/common/tableFunctions";
  import {buildBreadCrumb} from "@/common/tableFunctions";
  import routes from "@/common/routes";
  import {mapState} from "vuex";

  export default {
    name: "Publications",
    components: {
      "v-folder": Folder,
      "v-table": Table,
      "v-card": Card,
      "v-document": Document,
      "v-button": Button,
      "v-icon": Icon,
      'v-button-group': ButtonGroup,
      'v-checkbox': Checkbox,
      PageTemplate,
    },
    data() {
      return {
        table: {
          columns: [
            {
              label: 'Name',
              field: 'name',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Search',
                filterFn: columnFilter
              },
            },
            {
              label: 'Date Updated',
              field: 'dateUpdated',
              sortFn: dateSort_ddmmyyyy,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: columnFilter
              },
            },
            {
              label: 'Actions',
              field: 'actions',
              sortable: false,
              actions: 'view_and_download',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap',
            },
          ],
          rows: [],
          totalRecords: 0
        },
        activeRootFolder: null,
        activeSubFolder: null,
        disableBulkDownload: true,
        checkDownloadAll: false,
      };
    },
    computed: {
      ...mapState({
        subscriptions: state => state.publication.subscriptions,
        folder_id: state => state.publication.folderId,
        lastestSubscriptions: state => state.publication.latestSubscriptions,
        publications: state => state.publication.publication,
        recentPublications: state => state.publication.recentPublications,
        rootFolders: state => state.publication.rootFolders,
        subFolders: state => state.publication.subFolders,
        documents: state => state.publication.documents
      }),
    },
    mounted() {
      this.updateBreadCrumb();
      //this.getSubscription();
      this.getPublicationCategories();
      this.getRecentUpdates();
    },
    methods: {
      clearSelectedRoot() {
        this.activeRootFolder = null;
      },
      clearSelectedSub() {
        this.activeSubFolder = null;
        this.checkDownloadAll = false;
      },
      clearAllSelected() {
        this.clearSelectedRoot();
        this.clearSelectedSub();
      },
      downloadSelected: function (params) {
        let res = this.documents.filter(ele => ele.isSelected).map(ele => ele.id);
        this.$store.state.auth.isLoading = true;
        this.$store.dispatch(DOWNLOAD_BULK_PUBLICATION, res).then(res => {
          this.$notify({
              title: 'Success',
              text: res.message,
              type: 'success'
            });
        }).catch(err => {
          this.$notify({
                title: 'Error',
                text: err.message,
                type: 'error'
              });
        }).finally(() => {
          this.selectAllAction({target: {checked:false}});
            this.$store.state.auth.isLoading = false;
          });
      },
      downloadDoc: function (params) {
        let id = params;
        this.$store.state.auth.isLoading = true;
        this.$store.dispatch(DOWNLOAD_PUBLICATION, {id})
          .then(response => {
            return response;
          }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      previewDoc(params) {
        // If Microsoft Edge below 50 version
        if(window.navigator && window.navigator.msSaveOrOpenBlob) {
            this.downloadDoc(params);
            return;
        }
        let id = params;
        this.$store.state.auth.isLoading = true;
        this.$store.dispatch(PREVIEW_PUBLICATION, {id})
          .then(response => {
              window.open(response, '_blank');
          })
          .catch(error => {
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          })
          .finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      selectAllAction(evt) {
        this.checkDownloadAll = evt.target.checked;
        this.documents.map(ele => {
          ele.isSelected = evt.target.checked;
        });
        this.disableBulkDownload = !evt.target.checked;
      },
      togglePublicationSelect: function (evt) {
        let res = true;
        this.documents.map(ele => {
          if (ele.id == evt.target.id) {
            ele.isSelected = evt.target.checked;
          }
          if (ele.isSelected) {
            res = false;
          }
        });
        this.disableBulkDownload = res;
      },
      updateBreadCrumb: function () {
        let breadcrumbs = [];
        breadcrumbs['Dashboard'] = routes.DASHBOARD;
        breadcrumbs['Publications'] = '';
        this.$store.dispatch('updateBreadCrumbAction', buildBreadCrumb(breadcrumbs));
      },
      getPublicationCategories() {
        this.$store.dispatch(GET_PUBLICATION_CATEGORIES);
      },
      getRecentUpdates() {
        this.$store.dispatch(GET_RECENT_PUBLICATIONS)
          .then(response => {
            this.table.rows = this.recentPublications;
          });
      },
      loadDocuments(subFolderId, index) {
        this.activeSubFolder = index;
        this.$store.dispatch(GET_SUBCAT_PUBLICATIONS, subFolderId);
      },
      loadSubFolder(rootFolderId, index) {
        this.clearAllSelected();
        this.activeRootFolder = index;
        this.$store.dispatch(GET_PUBLICATION_SUBFOLDERS, rootFolderId);
      },
      toSubscriptions() {
        this.$router.push(routes.SUBSCRIPTIONS)
      }
    }
  }
</script>

